import { forwardRef } from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * Menu — portal dropdown menu on @radix-ui/react-dropdown-menu.
 *
 *   <Menu>
 *     <MenuTrigger asChild><Button size="sm">Actions</Button></MenuTrigger>
 *     <MenuContent align="start">
 *       <MenuLabel>Booking</MenuLabel>
 *       <MenuItem onSelect={open}>Open</MenuItem>
 *       <MenuItem disabled>No access</MenuItem>
 *       <MenuSeparator />
 *       <MenuItem tone="destructive" hint="⌫">Delete</MenuItem>
 *     </MenuContent>
 *   </Menu>
 *
 * `MenuTrigger` is an unstyled passthrough — wrap a Button/icon button
 * with `asChild` (the portal atoms never restyle the anchor control).
 */

export const menuItemVariants = cva(
  "relative flex w-full cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 type-body-md text-content-primary outline-none",
  {
    variants: {
      tone: {
        /** Keyboard/hover highlight rides the pressed wash — visible on the
            raised menu surface in both modes. */
        default:
          "data-highlighted:bg-surface-pressed data-highlighted:text-content-primary",
        /** Destructive actions — error text on an error wash when highlighted. */
        destructive:
          "text-content-error data-highlighted:bg-surface-error-soft data-highlighted:text-content-error",
      },
    },
    defaultVariants: {
      tone: "default",
    },
  },
);

export interface MenuProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root> {}

/** Controlled/uncontrolled root. Renders no DOM element. */
export function Menu(props: MenuProps) {
  return <DropdownMenuPrimitive.Root {...props} />;
}
Menu.displayName = "Menu";

export interface MenuTriggerProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> {}

/** Unstyled anchor — use `asChild` around a Button or icon button. */
export const MenuTrigger = forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Trigger>,
  MenuTriggerProps
>(({ className, ...props }, ref) => {
  return (
    <DropdownMenuPrimitive.Trigger ref={ref} className={className} {...props} />
  );
});
MenuTrigger.displayName = "MenuTrigger";

export interface MenuContentProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> {}

/** Portalled raised panel — same radius/border grammar as the Select popover. */
export const MenuContent = forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Content>,
  MenuContentProps
>(({ className, sideOffset = 4, ...props }, ref) => {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        collisionPadding={8}
        className={cn(
          "z-(--z-popover) min-w-44 max-h-96 overflow-y-auto rounded-lg border border-border-default bg-surface-raised p-1 shadow-menu outline-none",
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  );
});
MenuContent.displayName = "MenuContent";

export interface MenuItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item>,
    VariantProps<typeof menuItemVariants> {
  /** Right-aligned keyboard-hint slot (e.g. "⌘⌫"). */
  hint?: React.ReactNode;
}

export const MenuItem = forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Item>,
  MenuItemProps
>(({ className, tone, hint, children, ...props }, ref) => {
  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cn(
        menuItemVariants({ tone }),
        "data-disabled:pointer-events-none data-disabled:text-content-disabled",
        className,
      )}
      {...props}
    >
      {children}
      {hint ? (
        <span className="ml-auto pl-4 type-caption text-content-secondary">
          {hint}
        </span>
      ) : null}
    </DropdownMenuPrimitive.Item>
  );
});
MenuItem.displayName = "MenuItem";

export interface MenuSeparatorProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator> {}

/** Hairline between menu groups — border-default like Divider. */
export const MenuSeparator = forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Separator>,
  MenuSeparatorProps
>(({ className, ...props }, ref) => {
  return (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={cn("mx-2 my-1 h-px bg-border-default", className)}
      {...props}
    />
  );
});
MenuSeparator.displayName = "MenuSeparator";

export interface MenuLabelProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> {}

/** Non-interactive group heading — same grammar as SelectLabel. */
export const MenuLabel = forwardRef<
  React.ComponentRef<typeof DropdownMenuPrimitive.Label>,
  MenuLabelProps
>(({ className, ...props }, ref) => {
  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cn("type-overline text-content-secondary px-2 py-1.5", className)}
      {...props}
    />
  );
});
MenuLabel.displayName = "MenuLabel";
